/**
 * Explorer → map hop for "show this aircraft's track".
 *
 * Follows the VAR precedent (`intellifire:var-view-on-map`, dispatched by
 * VARExplorer and consumed by VARLayer): the explorer never touches the map
 * instance, and the layer that owns the drawing owns the listening.
 */
export const AFF_SHOW_TRACK_EVENT = 'intellifire:aff-show-track';

/**
 * The span a drawn track covers (#801, #866): a window back from now or an
 * explicit range, and the registration when the track is the aircraft's
 * rather than one tracker's. Carried with the track from draw to drawer entry
 * to retime, so a redraw asks for the same aircraft it first drew.
 */
export interface AffTrackSpan {
  hours?: number;
  start?: string;
  end?: string;
  registration?: string;
}

/**
 * Either a fixed window measured back from now, or an explicit range (#801).
 * Never both: they are different requests, and a half-specified range is
 * neither, so the modal will not dispatch one.
 */
export interface AffShowTrackDetail {
  esn: string;
  /** Aircraft display name, used to label the drawer entry (#802). */
  name?: string;
  /**
   * #866 — the aircraft's registration, so the track drawn is the aircraft's
   * across every tracker it carried, not one tracker's. Absent: by ESN.
   */
  registration?: string;
  /** One of the durations offered by the detail modal: 24, 48 or 72. */
  hours?: number;
  /** Inclusive start of a custom range, as the picker supplies it. */
  start?: string;
  /** Inclusive end of a custom range. */
  end?: string;
}

export const dispatchAffShowTrack = (detail: AffShowTrackDetail): void => {
  window.dispatchEvent(new CustomEvent(AFF_SHOW_TRACK_EVENT, { detail }));
};

/** Ask the AFF layer to remove every drawn track. */
export const AFF_CLEAR_TRACKS_EVENT = 'intellifire:aff-clear-tracks';

export const dispatchAffClearTracks = (): void => {
  window.dispatchEvent(new CustomEvent(AFF_CLEAR_TRACKS_EVENT));
};

/**
 * Ask for an aircraft's detail modal, from anywhere.
 *
 * Both the explorer's per-row View button and a click on the aircraft's map
 * marker raise this, so the two routes show the same thing rather than the map
 * having its own weaker popup.
 */
export const AFF_SHOW_AIRCRAFT_EVENT = 'intellifire:aff-show-aircraft';

/** Which level the aircraft view opens at (#799). */
export type AffAircraftLevel = 'summary' | 'detail';

export interface AffShowAircraftOptions {
  /**
   * Height of the marker icon, so an anchored panel clears its bottom edge by a
   * constant gap (#805). AFF icons range 30-50px, so an offset measured from the
   * aircraft's point alone spaced differently per aircraft type.
   */
  iconHeight?: number;
  /**
   * Level to open at (#799). A grid row already shows the identity fields the
   * summary repeats, so View there goes straight to detail; a map icon click has
   * no row context and opens the summary first. Defaults to summary.
   */
  level?: AffAircraftLevel;
}

export const dispatchAffShowAircraft = (
  aircraft: unknown,
  options: AffShowAircraftOptions = {},
): void => {
  window.dispatchEvent(
    new CustomEvent(AFF_SHOW_AIRCRAFT_EVENT, {
      detail: { aircraft, iconHeight: options.iconHeight, level: options.level ?? 'summary' },
    }),
  );
};

/** Ask the map to fly to one historical position and mark it. */
export const AFF_LOCATE_POINT_EVENT = 'intellifire:aff-locate-point';

export interface AffLocatePointDetail {
  lat: number;
  lon: number;
  name?: string;
  esn?: string;
  observed?: string;
}

export const dispatchAffLocatePoint = (detail: AffLocatePointDetail): void => {
  window.dispatchEvent(new CustomEvent(AFF_LOCATE_POINT_EVENT, { detail }));
};

/**
 * Map → drawer hop for a track that has just been fetched and is ready to draw
 * (#802). AffLayerProvider owns the fetch, the colour choice and the track id;
 * the drawer-side sync component owns registering it as a registry layer, which
 * is what performs the actual mapbox drawing. Keeping the announcement one-way
 * mirrors the explorer → map direction above: neither side holds the other.
 */
export const AFF_TRACK_DRAWN_EVENT = 'intellifire:aff-track-drawn';

export interface AffTrackDrawnDetail {
  trackId: string;
  esn: string;
  /** Human-readable label for the drawer entry. Falls back to the ESN. */
  aircraftName: string;
  data: GeoJSON.Feature | GeoJSON.FeatureCollection;
  color: string;
  halo: string;
  /** The span this track was fetched for, so the entry's control opens on it. */
  span?: AffTrackSpan;
}

export const dispatchAffTrackDrawn = (detail: AffTrackDrawnDetail): void => {
  window.dispatchEvent(new CustomEvent(AFF_TRACK_DRAWN_EVENT, { detail }));
};

/** A single drawn track is gone. Clear Tracks raises one of these per track. */
export const AFF_TRACK_REMOVED_EVENT = 'intellifire:aff-track-removed';

export interface AffTrackRemovedDetail {
  trackId: string;
}

export const dispatchAffTrackRemoved = (detail: AffTrackRemovedDetail): void => {
  window.dispatchEvent(new CustomEvent(AFF_TRACK_REMOVED_EVENT, { detail }));
};

/**
 * Ask for one already-drawn track to be redrawn over a different range (#802).
 *
 * Raised by the track's own layer-drawer entry. It carries the existing
 * trackId, so the track is updated in place: re-running the draw would mint a
 * new track-<esn>-<Date.now()> id and leave a second entry behind, losing the
 * opacity and z-order the user had set.
 */
export const AFF_TRACK_RETIME_EVENT = 'intellifire:aff-track-retime';

export interface AffTrackRetimeDetail {
  trackId: string;
  esn: string;
  aircraftName: string;
  span: AffTrackSpan;
}

export const dispatchAffTrackRetime = (detail: AffTrackRetimeDetail): void => {
  window.dispatchEvent(new CustomEvent(AFF_TRACK_RETIME_EVENT, { detail }));
};

/**
 * How long an aircraft may go without reporting before it drops off the live
 * map (#822).
 *
 * Raised by the live AFF layer's drawer entry, and once at startup by whatever
 * component knows the signed-in user. The control cannot own the fetch itself:
 * it exists only while its layer card is expanded, whereas the window has to
 * apply from the very first refresh. So the choice is announced and
 * AffLayerProvider — which owns the fetch — acts on it.
 *
 * The window is already validated against the server's range before it is
 * announced; a window outside it is never raised.
 */
export const AFF_LIVE_WINDOW_EVENT = 'intellifire:aff-live-window';

export interface AffLiveWindowDetail {
  /** Whole days, 1-120. Matches affRouter GET /ac. */
  days: number;
}

export const dispatchAffLiveWindow = (detail: AffLiveWindowDetail): void => {
  window.dispatchEvent(new CustomEvent(AFF_LIVE_WINDOW_EVENT, { detail }));
};
